import type { FormField } from '@lucid/lego';
import type { BusinessSearchResult } from '../../../../../api/services/lupp/order/dto.order';
import {
  getFinancingTypeOptions,
  getFinancingSubtypeOptions,
  getApplicationStatusOptions,
  getApplicationDecisionOptions,
} from './constants';

/**
 * Creates form fields configuration for Financing form
 *
 * @param labels - i18n labels for form field labels and placeholders
 * @param defaultValues - Default values for financing fields
 * @param theme - Theme variant (light or dark)
 * @param onLienholderChange - Callback when business name is selected
 * @param isDisabled - Whether the form should be disabled (based on applicationSource)
 * @returns Array of FormField configurations
 */
export const createFinancingFormFields = (
  labels?: Record<string, string>,
  defaultValues?: {
    lienholderName?: string;
    financingType?: string;
    financingSubtype?: string;
    applicationStatus?: string;
    applicationDecision?: string;
    partnerAppId?: string;
    downpayment?: string;
    amountFinanced?: string;
  },
  theme: 'light' | 'dark' = 'dark',
  onLienholderChange?: (business: BusinessSearchResult | null) => void,
  isDisabled: boolean = false
): FormField[] => {
  const financingTypeOptions = getFinancingTypeOptions(labels);
  const financingSubtypeOptions = getFinancingSubtypeOptions(labels);
  const applicationStatusOptions = getApplicationStatusOptions(labels);
  const applicationDecisionOptions = getApplicationDecisionOptions(labels);

  return [
    {
      id: 'financingGroup',
      fieldType: 'group',
      title: labels?.['form.financing'] ?? 'Financing',
      width: 'full',
      children: [
        {
          id: 'lienholderName',
          fieldType: 'custom',
          component: 'BusinessNameSearch',
          name: 'lienholderName',
          apiKey: 'lienholderName',
          required: true,
          width: 'third',
          componentProps: {
            disabled: isDisabled,
            label: labels?.['form.lienholderName'] ?? 'Lienholder Name',
            placeholder: labels?.['form.searchCompanyPlaceholder'] ?? 'Search for company...',
            required: true,
            dataTestId: 'lienholder-name-search',
            defaultValue: defaultValues?.lienholderName
              ? {
                  name: defaultValues.lienholderName,
                  duns: '',
                  primaryName: defaultValues.lienholderName,
                  isOutOfBusiness: false,
                  country: '',
                  countryCode: '',
                  displayText: defaultValues.lienholderName,
                }
              : null,
            onChange: onLienholderChange,
            theme: theme === 'dark' ? 'light' : 'dark',
            messages: {
              searchingText: labels?.['form.searching'] ?? 'Searching...',
              apiErrorText:
                labels?.['form.searchUnavailable'] ??
                'Search is temporarily unavailable. Please try again later.',
              minCharsText:
                labels?.['form.minCharsToSearch'] ?? 'Type at least 3 characters to search',
              noResultsText: labels?.['form.noResults'] ?? 'No matching results found.',
              validationRequiredText:
                labels?.['validation.selectValidCompany'] ??
                'Please select a valid Company from the list.',
            },
          },
          validationMessages: {
            required:
              labels?.['form.fieldRequired'] ??
              labels?.['validation.selectValidCompany'] ??
              'Please select a valid Company from the list.',
          },
        },
        {
          id: 'financingType',
          fieldType: 'dropdown',
          name: 'financingType',
          apiKey: 'financingType',
          label: labels?.['form.financingType'] ?? 'Financing Type',
          required: true,
          disabled: isDisabled,
          width: 'third',
          options: financingTypeOptions,
          defaultValue: defaultValues?.financingType?.toLowerCase(),
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'financingSubtype',
          fieldType: 'dropdown',
          name: 'financingSubtype',
          apiKey: 'financingSubtype',
          label: labels?.['form.financingSubtype'] ?? 'Financing Subtype',
          required: true,
          disabled: isDisabled,
          width: 'third',
          options: financingSubtypeOptions,
          defaultValue: defaultValues?.financingSubtype?.toLowerCase(),
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'applicationStatus',
          fieldType: 'dropdown',
          name: 'applicationStatus',
          apiKey: 'applicationStatus',
          label: labels?.['form.applicationStatus'] ?? 'Application Status',
          required: true,
          disabled: isDisabled,
          width: 'half',
          options: applicationStatusOptions,
          defaultValue: defaultValues?.applicationStatus?.toLowerCase(),
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'applicationDecision',
          fieldType: 'dropdown',
          name: 'applicationDecision',
          apiKey: 'applicationDecision',
          label: labels?.['form.applicationDecision'] ?? 'Application Decision',
          required: true,
          disabled: isDisabled,
          width: 'half',
          options: applicationDecisionOptions,
          defaultValue: defaultValues?.applicationDecision?.toLowerCase(),
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'partnerAppId',
          fieldType: 'text',
          name: 'partnerAppId',
          apiKey: 'partnerAppId',
          label: labels?.['form.partnerAppId'] ?? 'Partner App ID',
          required: true,
          disabled: isDisabled,
          width: 'third',
          defaultValue: defaultValues?.partnerAppId,
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'downpayment',
          fieldType: 'text',
          name: 'downpayment',
          apiKey: 'downpayment',
          label: labels?.['form.downpayment'] ?? 'Downpayment',
          required: true,
          disabled: isDisabled,
          width: 'third',
          defaultValue: defaultValues?.downpayment,
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
        {
          id: 'amountFinanced',
          fieldType: 'text',
          name: 'amountFinanced',
          apiKey: 'amountFinanced',
          label: labels?.['form.amountFinanced'] ?? 'Amount Financed',
          required: true,
          disabled: isDisabled,
          width: 'third',
          defaultValue: defaultValues?.amountFinanced,
          validationMessages: {
            required: labels?.['form.fieldRequired'] ?? 'This field is required',
          },
        },
      ],
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      buttons: [
        {
          id: 'submitButton',
          label: labels?.['form.save'] ?? 'Save',
          type: 'primary',
          size: 'medium',
          color: 'neutral',
          theme: 'light',
          isFluid: true,
          disabled: isDisabled,
        },
      ],
    },
  ];
};
